import { DEFAULT_API_URL } from "./config";
import type { Connection } from "./types";

const API_URL_KEY = "goodness.apiUrl";
const ORGANIZATION_ID_KEY = "goodness.organizationId";
const TOKEN_KEY = "goodness.token";

let memoryToken = "";

function browserStorage(type: "localStorage" | "sessionStorage") {
  if (typeof window === "undefined") return null;
  try {
    return window[type];
  } catch {
    return null;
  }
}

function read(type: "localStorage" | "sessionStorage", key: string) {
  try {
    return browserStorage(type)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(
  type: "localStorage" | "sessionStorage",
  key: string,
  value: string,
) {
  try {
    browserStorage(type)?.setItem(key, value);
  } catch {
    // Storage may be disabled. The token remains available in memory for this page.
  }
}

export function loadStoredConnection(): Connection {
  // Keep existing sessions working while migrating the token from the old
  // tab-only storage to persistent browser storage.
  const storedToken =
    read("localStorage", TOKEN_KEY) ?? read("sessionStorage", TOKEN_KEY);
  if (storedToken !== null) {
    memoryToken = storedToken;
    write("localStorage", TOKEN_KEY, storedToken);
  }

  return {
    apiUrl: read("localStorage", API_URL_KEY) ?? DEFAULT_API_URL,
    organizationId: read("localStorage", ORGANIZATION_ID_KEY) ?? "",
    token: storedToken ?? memoryToken,
  };
}

export function storeConnection(connection: Connection) {
  memoryToken = connection.token;
  write("localStorage", API_URL_KEY, connection.apiUrl);
  write("localStorage", ORGANIZATION_ID_KEY, connection.organizationId);
  write("localStorage", TOKEN_KEY, connection.token);
}

export function storeOrganizationId(organizationId: string) {
  write("localStorage", ORGANIZATION_ID_KEY, organizationId);
}

export function clearStoredToken() {
  memoryToken = "";
  try {
    browserStorage("localStorage")?.removeItem(TOKEN_KEY);
  } catch {
    // The in-memory copy has already been cleared.
  }
  try {
    browserStorage("sessionStorage")?.removeItem(TOKEN_KEY);
  } catch {
    // Storage may be disabled.
  }
}
